import { NumberFormatService } from 'src/app/core/services/number-format.service';

/** Shared impure-pipe memoization for the number/currency display pipes. Impure pipes are
 *  re-invoked on EVERY change-detection tick and one instance lives per binding (per cell in
 *  a large table), so each pipe caches its last result and only re-runs the (non-trivial)
 *  format work when its args or the active preset actually change. Holding the mechanism here
 *  keeps the two pipes from carrying divergent copies of it. */
export abstract class MemoizedNumberPipe {
  // Args are held in fixed positional slots (not an array) so a cache-hit tick — the
  // overwhelmingly common case, once per cell per CD pass — allocates nothing. appCurrency
  // uses all four (value, currencyCode, display, digitsInfo); appNumber uses the first two.
  private cachedA: unknown;
  private cachedB: unknown;
  private cachedC: unknown;
  private cachedD: unknown;
  private cacheFormat?: string;
  private cacheResult: string | null = null;
  private primed = false;

  constructor(protected numberFormat: NumberFormatService) {}

  /** Positional args identify the call (compared with Object.is); the active preset is folded
   *  in here so a cross-tab preset change busts the cache without every caller repeating it. On a
   *  cache hit this only does cheap scalar compares and builds nothing; `compute()` runs only on a
   *  genuine miss. Args are passed positionally rather than as an array so no per-tick array is
   *  allocated (pure GC churn across the hundreds of cells in a large table). Object.is (not `!==`)
   *  so a repeated NaN value still counts as a hit — otherwise a NaN-valued cell would re-format
   *  every tick. `primed` gates the first call, since `undefined` is a legitimate arg value. */
  protected memoized(
    compute: () => string | null, a?: unknown, b?: unknown, c?: unknown, d?: unknown,
  ): string | null {
    const format = this.numberFormat.currentNumberFormat;
    if (
      !this.primed || this.cacheFormat !== format ||
      !Object.is(this.cachedA, a) || !Object.is(this.cachedB, b) ||
      !Object.is(this.cachedC, c) || !Object.is(this.cachedD, d)
    ) {
      this.primed = true;
      this.cacheFormat = format;
      this.cachedA = a;
      this.cachedB = b;
      this.cachedC = c;
      this.cachedD = d;
      this.cacheResult = compute();
    }
    return this.cacheResult;
  }
}
